import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Home,
  Package,
  Menu,
  ChevronLeft,
  ChevronDown,
  Users,
  DollarSign,
  UserSearch,
  ClipboardList,
  PackageCheckIcon,
  PackageSearchIcon,
  HistoryIcon,
  TrendingUp,
  CalendarClock,
  Eye,
  BadgePercent,
  Tags,
  FileSpreadsheet,
  UsersRound,
  UserRound,
} from "lucide-react";
import { isOrderItemsReportEnabled } from "@/features/orderItemsReportFeature";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { NavLink } from "../NavLink";
import { useAuth } from "react-oidc-context";

import { SchoolUnit } from "./SchoolUnit";
import { isCouponEnabled } from "@/features/couponFeature";
import { isCustomerGroupAddressEnabled } from "@/features/customerGroupAddressFeature";
import { isPhysicalClientEnabled } from "@/features/physicalClientFeature";
import { isDevelopmentEnvironment } from "@/lib/env";

// Tipos para os itens do menu
type MenuItem = {
  isSeparator?: false; // Item de menu padrão
  title: string;
  href?: string;
  icon: React.ComponentType;
  disabled: boolean;
  tooltip?: string;
  children?: MenuItem[];
  end?: boolean;
};

type MenuSeparator = {
  isSeparator: true;
  title: string; // Título da seção (ex: "BackOffice")
  href?: string;
};

type NavItem = MenuItem | MenuSeparator;


interface GetConfigProps {
  title: string;
  dashboard: 'BACKOFFICE' | 'CUSTOMER_PORTAL';
  menuItems: NavItem[];
}

const getConfig = (user: ReturnType<typeof useAuth>['user']): GetConfigProps => {
  const isBackoffice = window.location.pathname.includes('backoffice');
  const role = user?.profile?.["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];

  const menuBackoffice: MenuItem[] = [
    {
      title: "Pedidos",
      icon: PackageCheckIcon,
      disabled: false,
      children: [
        {
          title: "Pedidos Realizados",
          href: "/backoffice/pedidos",
          icon: Package,
          disabled: false,
        },
        {
          title: "Pedidos programados",
          href: "/backoffice/pedido-programados",
          icon: HistoryIcon,
          disabled: false,
        },
        // {
        //   title: "Monitoramento de Simulados",
        //   href: "/backoffice/simulados",
        //   icon: ClipboardList,
        //   disabled: false,
        // }
      ]
    },
    isCustomerGroupAddressEnabled
      ? {
        title: "Clientes",
        icon: UserSearch,
        disabled: false,
        children: [

          {
            // Com o cadastro de pessoa física, a lista atual (CNPJ) passa a se chamar "Pessoa jurídica".
            title: isPhysicalClientEnabled ? "Pessoa jurídica" : "Lista de clientes",
            href: "/backoffice/clients",
            icon: UserSearch,
            disabled: false,
          },
          ...(isPhysicalClientEnabled
            ? [
              {
                title: "Pessoa física",
                href: "/backoffice/clientes/pf",
                icon: UserRound,
                disabled: false,
              },
            ]
            : []),
          {
            title: "Grupos de cliente",
            href: "/backoffice/clientes/grupos",
            icon: UsersRound,
            disabled: false,
          },
        ],
      }
      : {
        title: "Clientes",
        href: "/backoffice/clients",
        icon: UserSearch,
        disabled: false,
        tooltip: "Em desenvolvimento",
      },
    {
      title: "Perfis de Cliente",
      href: "/backoffice/perfil-clientes",
      icon: Users,
      disabled: false,
      tooltip: "Em desenvolvimento",
    },
    {
      title: "Gestão Comercial",
      icon: Tags,
      disabled: false,
      children: [
        ...(isCouponEnabled
          ? [{
            title: "Gestão de Cupons",
            href: "/backoffice/gestao-comercial/cupons",
            icon: BadgePercent,
            disabled: false,
          }]
          : []),
        {
          title: "Faturamento por cliente",
          href: "/backoffice/gestao-comercial/faturamento-clientes",
          icon: TrendingUp,
          disabled: false,
        },
      ],
    },
    ...(isOrderItemsReportEnabled
      ? [
        {
          title: "Relatórios",
          icon: FileSpreadsheet,
          disabled: false,
          children: [
            {
              title: "Itens de Pedido",
              href: "/backoffice/relatorios/itens-pedido",
              icon: FileSpreadsheet,
              disabled: false,
            },
          ],
        },
      ]
      : []),
    // {
    //   title: "Reajuste",
    //   href: "/backoffice/reajuste",
    //   icon: TrendingUp,
    //   disabled: false,
    // }
    // {
    //   title: "Ciclos Comerciais",
    //   href: "/backoffice/ciclos-comerciais",
    //   icon: CalendarClock,
    //   disabled: false,
    // }
  ];

  const menuCustomerPortal: MenuItem[] = [
    {
      title: "Início",
      href: "/customer-portal",
      icon: Home,
      disabled: false,
      end: true,
    },
    // {
    //   title: "Tabela de Preços",
    //   href: "/customer-portal/tabela-precos",
    //   icon: TrendingUp,
    //   disabled: false,
    // },
    {
      title: "Meus Pedidos",
      icon: PackageCheckIcon,
      disabled: false,
      children: [
        {
          title: "Pedidos Realizados",
          href: "/customer-portal/pedidos",
          icon: PackageCheckIcon,
          disabled: false,
        },
        {
          title: "Pedidos Programados",
          href: "/customer-portal/pedido-programado",
          icon: HistoryIcon,
          disabled: false,
        },
      ]
    },
    {
      title: "Realizar Pedido",
      href: "/customer-portal/realizar-pedido",
      icon: PackageSearchIcon,
      disabled: false,
      end: true,
    },
    {
      title: "Financeiro",
      href: "/customer-portal/financeiro",
      icon: DollarSign,
      disabled: false,
    }
  ];

  if (role === 'Administrator') {
    return {
      title: isBackoffice ? 'Backoffice' : 'Portal do Cliente',
      dashboard: isBackoffice ? 'BACKOFFICE' : 'CUSTOMER_PORTAL',
      menuItems: [
        {
          isSeparator: true,
          title: 'BackOffice',
          href: isDevelopmentEnvironment ? '/admin-pedidos' : undefined,
        },
        ...menuBackoffice,
        { isSeparator: true, title: 'Portal do Cliente' },
        ...menuCustomerPortal
      ],
    };
  }

  return {
    title: isBackoffice ? 'Backoffice' : 'Portal do Cliente',
    dashboard: isBackoffice ? 'BACKOFFICE' : 'CUSTOMER_PORTAL',
    menuItems: isBackoffice ? menuBackoffice : menuCustomerPortal,
  }
}

export default function CustomerPortalLayout() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  // Memoiza as configurações do menu para evitar re-renderizações infinitas no useEffect
  const { menuItems, title, dashboard } = useMemo(() => getConfig(user), [user]);

  const [openSubMenus, setOpenSubMenus] = useState<Record<string, boolean>>({});

  // Auto-expande o submenu APENAS quando o usuário navega para uma nova rota
  useEffect(() => {
    menuItems.forEach(item => {
      if (!item.isSeparator && item.children?.some(child => location.pathname === child.href)) {
        setOpenSubMenus(prev => ({ ...prev, [item.title]: true }));
      }
    });
  }, [location.pathname, menuItems]);

  const toggleSubMenu = (title: string) => {
    setOpenSubMenus(prev => ({
      ...prev,
      [title]: !prev[title]
    }));
  };

  return (
    <TooltipProvider>
      <div className="h-screen flex w-full bg-background relative">
        {/* Skip to Content Link - WCAG 2.4.1 */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
        >
          Pular para o conteúdo
        </a>

        {/* Sidebar */}
        <aside
          className={cn(
            "border-r bg-card transition-all duration-300 flex flex-col",
            collapsed ? "w-16" : "w-64"
          )}
        >
          {/* Logo/Header */}
          <div className="h-16 flex items-center justify-between px-4 border-b">
            {!collapsed && (
              <a href="/" className="flex items-center" aria-label={`Ir para o início do ${title}`}>
                <span className="font-semibold text-lg">{title}</span>
              </a>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCollapsed(!collapsed)}
              className="ml-auto"
              aria-label={collapsed ? "Expandir menu lateral" : "Recolher menu lateral"}
              aria-expanded={!collapsed}
              aria-controls="sidebar-nav"
            >
              {collapsed ? (
                <Menu className="h-5 w-5" aria-hidden="true" />
              ) : (
                <ChevronLeft className="h-5 w-5" aria-hidden="true" />
              )}
            </Button>
          </div>

          {/* Navigation */}
          <nav id="sidebar-nav" aria-label="Navegação principal" className="flex-1 p-3 space-y-1 overflow-y-auto">
            <ul className="space-y-1 list-none p-0 m-0">
              {menuItems.map((item, index) => {
                // Renderiza o separador de seção
                if (item.isSeparator) {
                  if (collapsed) return null;
                  return (
                    <li key={`separator-${item.title}`} className="py-2 mt-4 first:mt-0" role="presentation">
                      {index > 0 && <hr className="border-muted-foreground/20 mb-4" aria-hidden="true" />}
                      {item.href ? (
                        <NavLink
                          to={item.href}
                          className="ml-3 text-muted-foreground text-xs font-bold uppercase tracking-wider"
                          aria-label="Abrir Painel Administrativo"
                        >
                          {item.title}
                        </NavLink>
                      ) : (
                        <span className="ml-3 text-muted-foreground text-xs font-bold uppercase tracking-wider">{item.title}</span>
                      )}
                    </li>
                  );
                }

                // Renderiza o item de menu normal
                const Icon = item.icon;
                const hasChildren = item.children && item.children.length > 0;
                const isOpen = openSubMenus[item.title];
                const isChildActive = hasChildren && item.children?.some(child => location.pathname === child.href);
                const submenuId = `submenu-${item.title.replace(/\s+/g, '-').toLowerCase()}`;

                const navLinkContent = (
                  <>
                    <Icon className={cn("h-5 w-5 shrink-0", isChildActive && collapsed && "text-primary-foreground")} aria-hidden="true" />
                    {!collapsed && (
                      <div className="flex-1 flex items-center justify-between min-w-0">
                        <span className="truncate">{item.title}</span>
                        {hasChildren && (
                          <ChevronDown
                            className={cn(
                              "h-4 w-4 transition-transform duration-200",
                              isOpen && "rotate-180"
                            )}
                            aria-hidden="true"
                          />
                        )}
                      </div>
                    )}
                  </>
                );

                if (item.disabled) {
                  return (
                    <li key={item.title}>
                      <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                          <span
                            tabIndex={0}
                            role="link"
                            aria-disabled="true"
                            className={cn(
                              "flex items-center gap-3 px-3 py-2 rounded-md text-muted-foreground cursor-not-allowed opacity-50",
                              collapsed && "justify-center"
                            )}
                          >
                            {navLinkContent}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="right">
                          <p>{item.tooltip || "Em desenvolvimento"}</p>
                        </TooltipContent>
                      </Tooltip>
                    </li>
                  );
                }

                if (collapsed) {
                  if (hasChildren) {
                    return (
                      <li key={item.title}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              aria-label={item.title}
                              className={cn(
                                "flex items-center justify-center h-10 w-10 mx-auto rounded-md transition-colors",
                                isChildActive
                                  ? "bg-primary text-primary-foreground shadow-sm"
                                  : "text-foreground hover:bg-accent"
                              )}
                            >
                              <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent side="right" align="start" className="w-56 ml-2">
                            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground border-b mb-1">
                              {item.title}
                            </div>
                            {item.children?.map((child) => (
                              <DropdownMenuItem
                                key={child.href}
                                onClick={() => navigate(child.href!)}
                                className={cn(
                                  "flex items-center gap-2 cursor-pointer",
                                  location.pathname === child.href && "bg-primary/10 text-primary font-medium"
                                )}
                              >
                                <child.icon className="h-4 w-4" aria-hidden="true" />
                                <span>{child.title}</span>
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </li>
                    );
                  }

                  return (
                    <li key={`${item.title}-${item.href}`}>
                      <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                          <NavLink
                            to={item.href || "#"}
                            end={item.end}
                            className={cn(
                              "flex items-center justify-center h-10 w-10 mx-auto rounded-md transition-colors text-foreground hover:bg-accent"
                            )}
                            activeClassName="bg-primary text-primary-foreground shadow-sm"
                            aria-label={item.title}
                          >
                            {navLinkContent}
                          </NavLink>
                        </TooltipTrigger>
                        <TooltipContent side="right">
                          <p>{item.title}</p>
                        </TooltipContent>
                      </Tooltip>
                    </li>
                  );
                }

                if (hasChildren) {
                  return (
                    <li key={item.title} className="space-y-1">
                      <button
                        onClick={() => toggleSubMenu(item.title)}
                        aria-expanded={isOpen}
                        aria-controls={submenuId}
                        className={cn(
                          "flex items-center gap-3 px-3 py-2 rounded-md transition-colors w-full text-left",
                          isChildActive
                            ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm"
                            : "text-foreground hover:bg-accent"
                        )}
                      >
                        {navLinkContent}
                      </button>
                      {isOpen && (
                        <ul id={submenuId} className="pl-4 ml-3 border-l space-y-1 mt-1 list-none">
                          {item.children?.map((child) => (
                            <li key={child.href}>
                              <NavLink
                                to={child.href!}
                                end={child.end}
                                className="flex items-center gap-3 px-3 py-1.5 rounded-md text-foreground/80 hover:bg-accent hover:text-foreground transition-colors text-sm"
                                activeClassName="bg-primary/10 text-primary font-medium shadow-sm"
                              >
                                <child.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                                <span>{child.title}</span>
                              </NavLink>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                }

                return (
                  <li key={item.title}>
                    <NavLink
                      to={item.href || "#"}
                      end={item.end}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2 rounded-md transition-colors text-foreground hover:bg-accent"
                      )}
                      activeClassName="bg-primary text-primary-foreground shadow-sm"
                    >
                      {navLinkContent}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </nav>
        </aside>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0 overflow-x-hidden">
          {/* Header */}
          <header className="h-16 border-b bg-card px-6 flex items-center justify-between">
            <div></div>
            {dashboard === 'CUSTOMER_PORTAL' && <SchoolUnit />}
            {dashboard === 'BACKOFFICE' && <h2 className="text-xl font-semibold">Sistema de Gestão</h2>}
            <div className="flex items-center gap-4">
              <Avatar className="h-9 w-9">
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {user?.profile?.given_name?.charAt(0) || "U"}
                </AvatarFallback>
              </Avatar>
            </div>
          </header>

          {/* Page Content */}
          <main id="main-content" className="flex-1 overflow-auto" tabIndex={-1}>
            <Outlet />
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
}
